import { HeaderFlyoutInstance } from '@owc-packages/Header/src/HeaderFlyout';
import { owcFlyoutAnimationTime } from '@owc-packages/Header/src/styles/_flyout-vars.scss';
import Vue from 'vue';
import { HeaderEvents } from './headerEvents.enum';
import { FlyoutVisibilityEvent } from './headerIntegrations.d';
import { FlyoutInnerCloseEvent } from './flyoutIntegrations.d';
import { updateActiveFlyout } from './headerIntegrations';

export type FlyoutRoot<T> = ShadowRoot & {
  host: Element & T;
};

export type FlyoutHost = HTMLElement & {
  close: (global: boolean) => Promise<void>;
};

export const setupHostApi = (
  context: HeaderFlyoutInstance,
  transition = 'flyout-slide',
  duration = owcFlyoutAnimationTime,
) => {
  const close = async () => {
    const start = new Promise<void>(resolve => {
      // can this be removed perhaps?
      context.$el.classList.add('owc-header-flyout--allow-animation');
      context.$el.classList.add(`${transition}-leave`);
      resolve();
    });
    const during = new Promise<void>(resolve => {
      context.$el.classList.add(`${transition}-leave-active`);
      setTimeout(resolve, duration);
    });
    const end = new Promise<void>(resolve => {
      context.$el.classList.remove(`${transition}-leave`);
      context.$el.classList.add(`${transition}-leave-to`);
      resolve();
    });
    return new Promise<void>(resolve => {
      const result = [start, during, end].reduce((accumulatorPromise, next) => {
        return accumulatorPromise.then(() => {
          return next;
        });
      }, Promise.resolve());
      result.then(() => {
        resolve();
      });
    });
  };
  context.$nextTick(() => {
    const host = (context.$el?.getRootNode() as FlyoutRoot<FlyoutHost>).host;
    if (host) {
      host.close = close;
    }
  });
};

// should the listener be added automatically in setupHostApi?
export const dispatchCloseEvent = (context: Vue) => {
  context.$el.dispatchEvent(
    new CustomEvent(HeaderEvents.FLYOUT_CLOSE_EVENT, {
      composed: true,
      bubbles: true,
    }),
  );
};

/**
 * This method is used by the Header Flyout during the vmos
 * integration and does 3 things:
 *
 * updateActiveFlyout - updates the active flyout state on the header integration
 * file so that interacting with other integration or another vmos flyout properly
 * closes the current one
 *
 * dispatchEvent for the MAIN_FLYOUT_CLOSE_EVENT - updates the store state of the flyout
 * chain so that interacting with other flyouts or topics inside the flyout doesn't break
 * the flyout state chain
 *
 * dispatchCloseEvent so that vmos can control the show/hide behavior of the flyout.
 */
export const dispatchMainFlyoutCloseEvent = (context: Vue, detail: FlyoutInnerCloseEvent) => {
  updateActiveFlyout(
    context.$el,
    detail.flyoutId,
    detail.newStatus,
    detail.isInnerFlyout,
    detail.isBackInteraction,
  );
  context.$el.dispatchEvent(
    new CustomEvent(HeaderEvents.MAIN_FLYOUT_CLOSE_EVENT, {
      detail,
      composed: true,
      bubbles: true,
    }),
  );
  dispatchCloseEvent(context);
};

export const dispatchVisibilityEvent = (context: Vue, detail: FlyoutVisibilityEvent) => {
  context.$el.dispatchEvent(
    new CustomEvent<FlyoutVisibilityEvent>(HeaderEvents.FLYOUT_VISIBILITY_CHANGE, {
      detail,
      composed: true,
      bubbles: true,
    }),
  );
};
